"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelfOrderMenuSnapshot = exports.SelfOrderMenuChannel = exports.MenuItemPresentation = exports.MenuCategory = exports.ModifierGroup = exports.ModifierOption = exports.DietaryMetadata = exports.MenuImageRef = exports.MenuBadgeKey = exports.WELL_KNOWN_BADGE_KEYS = exports.MenuAvailabilityWindow = exports.SelfOrderType = exports.SelfOrderMenuChannelStatus = exports.LocalizedText = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
const terminal_js_1 = require("./terminal.js");
/**
 * Menu authoring and publication for the self-order food station
 * (self-order spec v2.3 §6.2).
 *
 * The existing `catalog` module remains the canonical sellable-item source.
 * Nothing here replaces it: a menu presentation *references* a catalog item and
 * adds what the catalog has no opinion about — which channel shows it, what the
 * customer-facing name is in each language, which modifier groups apply, what it
 * looks like on a kiosk, and when it is available.
 *
 * Money is integer minor units as a base-10 string (`MoneyMinor`), never a
 * float, and never a number that JSON would round at 2^53.
 */
/* -------------------------------------------------------------------------- */
/* Shared primitives                                                          */
/* -------------------------------------------------------------------------- */
/**
 * Customer-facing text, keyed by locale.
 *
 * A map rather than a `name` + `nameTranslations` pair, because there is no
 * privileged language: the channel names its `defaultLocale` and every lookup
 * falls back to it. Publication validation (not this schema) enforces that the
 * default locale is present — a zod refinement here cannot see the channel.
 */
exports.LocalizedText = zod_1.z.record(terminal_js_1.LocaleCode, zod_1.z.string().min(1).max(512));
exports.SelfOrderMenuChannelStatus = zod_1.z.enum(['draft', 'published', 'archived']);
/** Where the customer intends to eat. Drives tax in some packs, and routing in most. */
exports.SelfOrderType = zod_1.z.enum(['eat_in', 'takeaway']);
/**
 * A weekly availability window. `null` on a menu record means "always", which is
 * different from an empty array — an empty array is "never", and publication
 * validation rejects it rather than silently hiding an item.
 */
exports.MenuAvailabilityWindow = zod_1.z.object({
    /** ISO-8601 day number: 1 = Monday … 7 = Sunday. */
    dayOfWeek: zod_1.z.number().int().min(1).max(7),
    /** Local wall-clock at the location, `HH:MM`, inclusive. */
    startTime: zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    /** Local wall-clock at the location, `HH:MM`, exclusive. */
    endTime: zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});
/**
 * Badge keys the UI gives meaning to.
 *
 * Left as a constrained string rather than a closed enum, because a retailer
 * will want a badge we did not think of and a contract change is a poor way to
 * add the word "spicy". The spelling matters — the theme maps these to a fill
 * and the kiosk renders at most one per card (Amendment 1, D8).
 */
exports.WELL_KNOWN_BADGE_KEYS = {
    popular: 'popular',
    new: 'new',
    vegetarian: 'vegetarian',
    vegan: 'vegan',
    spicy: 'spicy',
};
exports.MenuBadgeKey = zod_1.z.string().min(1).max(32);
/**
 * A 16:9 product image, content-addressed (Amendment 1, D7).
 *
 * The hash is the identity: the terminal caches by it and re-fetches only when
 * it changes, which is what lets the menu snapshot carry an ETag. `null` is a
 * legitimate published state — the card falls back to the category tone with the
 * item's icon at the same aspect ratio, and the card's height does not change
 * between the two, so a mixed menu stays on one grid.
 */
exports.MenuImageRef = zod_1.z.object({
    /** Content hash, lowercase hex. The cache key on both sides. */
    contentHash: zod_1.z.string().regex(/^[0-9a-f]{32,128}$/),
    /** Storage key resolved by the platform; never a public URL in the contract. */
    storageKey: zod_1.z.string().min(1).max(512),
    width: zod_1.z.number().int().min(960),
    height: zod_1.z.number().int().min(540),
    byteSize: zod_1.z
        .number()
        .int()
        .min(1)
        .max(250 * 1024),
    contentType: zod_1.z.enum(['image/jpeg', 'image/webp']),
});
/* -------------------------------------------------------------------------- */
/* Deferred display metadata (Amendment 1, D4)                                */
/* -------------------------------------------------------------------------- */
/**
 * Allergen and dietary metadata.
 *
 * Carried from the first release, unpopulated and unrendered. Customer-facing
 * presentation is explicitly out of the first implementation slice: it needs
 * customer-supplied recipe data before any UI is worth drawing. These fields
 * exist now so the later change is data and copy rather than a relayout, and so
 * the contract round-trips them without a migration.
 *
 * Codes are free strings, not an enum — the allergen list is national law, not
 * ours to close.
 */
exports.DietaryMetadata = zod_1.z.object({
    allergens: zod_1.z.array(zod_1.z.string().min(1).max(64)).default([]),
    dietaryTags: zod_1.z.array(zod_1.z.string().min(1).max(64)).default([]),
});
/* -------------------------------------------------------------------------- */
/* Modifiers                                                                  */
/* -------------------------------------------------------------------------- */
exports.ModifierOption = zod_1.z.object({
    id: zod_1.z.string().min(1),
    label: exports.LocalizedText,
    /**
     * Signed delta applied to the line's unit price. Negative is legal — "no
     * cheese, minus 30 cents" is a real menu.
     */
    priceDeltaMinor: pricing_js_1.MoneyMinor.default('0'),
    isDefault: zod_1.z.boolean().default(false),
    isAvailable: zod_1.z.boolean().default(true),
    /** Free text handed to the kitchen, e.g. "no ice". Not customer-facing. */
    preparationText: zod_1.z.string().max(255).optional(),
    sortOrder: zod_1.z.number().int().min(0).default(0),
    dietary: exports.DietaryMetadata.optional(),
});
/**
 * A group of options presented together.
 *
 * `minimumSelections`/`maximumSelections` are the contract the terminal and the
 * platform both enforce (§6.2). They are validated in both places on purpose:
 * the terminal so the customer cannot build an impossible basket, the platform
 * because the terminal is not trusted.
 */
exports.ModifierGroup = zod_1.z
    .object({
    id: zod_1.z.string().min(1),
    name: exports.LocalizedText,
    helpText: exports.LocalizedText.optional(),
    minimumSelections: zod_1.z.number().int().min(0).default(0),
    maximumSelections: zod_1.z.number().int().min(1).default(1),
    required: zod_1.z.boolean().default(false),
    allowRepeatedOptions: zod_1.z.boolean().default(false),
    sortOrder: zod_1.z.number().int().min(0).default(0),
    options: zod_1.z.array(exports.ModifierOption).min(1),
})
    .superRefine((group, ctx) => {
    if (group.maximumSelections < group.minimumSelections) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['maximumSelections'],
            message: 'maximumSelections must be at least minimumSelections',
        });
    }
    if (group.required && group.minimumSelections < 1) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['minimumSelections'],
            message: 'a required group must ask for at least one selection',
        });
    }
    if (!group.allowRepeatedOptions && group.maximumSelections > group.options.length) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['maximumSelections'],
            message: 'cannot select more options than exist without allowRepeatedOptions',
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Categories and items                                                       */
/* -------------------------------------------------------------------------- */
exports.MenuCategory = zod_1.z.object({
    id: zod_1.z.string().min(1),
    menuChannelId: zod_1.z.string().min(1),
    parentId: zod_1.z.string().min(1).nullable().default(null),
    name: exports.LocalizedText,
    description: exports.LocalizedText.optional(),
    sortOrder: zod_1.z.number().int().min(0).default(0),
    /** Material Symbol key. The theme chooses the glyph set, not the meaning. */
    iconKey: zod_1.z.string().min(1).max(64).optional(),
    /** Semantic tone token, resolved by the active theme. Never a hex value. */
    toneKey: zod_1.z.string().min(1).max(64).optional(),
    isEnabled: zod_1.z.boolean().default(true),
    availability: zod_1.z.array(exports.MenuAvailabilityWindow).nullable().default(null),
});
exports.MenuItemPresentation = zod_1.z.object({
    id: zod_1.z.string().min(1),
    menuChannelId: zod_1.z.string().min(1),
    /** The canonical sellable item. Catalog stays the source of truth for it. */
    catalogItemId: zod_1.z.string().min(1),
    categoryId: zod_1.z.string().min(1),
    name: exports.LocalizedText,
    description: exports.LocalizedText.optional(),
    image: exports.MenuImageRef.nullable().default(null),
    /** Fallback when `image` is null; rendered on the category tone at 16:9. */
    iconKey: zod_1.z.string().min(1).max(64).optional(),
    /**
     * Channel price override in minor units. Null means "use the pricing module's
     * answer for this catalog item", which is the normal case — a menu channel
     * should not quietly become a second price list.
     */
    priceOverrideMinor: pricing_js_1.MoneyMinor.nullable().default(null),
    /**
     * Customer-visible unit price resolved by the platform at publication. This
     * is immutable with the menu content hash; submission prices the same bytes
     * the customer saw rather than consulting a mutable live price list.
     */
    unitPriceMinor: pricing_js_1.MoneyMinor,
    badges: zod_1.z.array(exports.MenuBadgeKey).max(4).default([]),
    modifierGroupIds: zod_1.z.array(zod_1.z.string().min(1)).default([]),
    isAvailable: zod_1.z.boolean().default(true),
    isSoldOut: zod_1.z.boolean().default(false),
    availability: zod_1.z.array(exports.MenuAvailabilityWindow).nullable().default(null),
    /** Which kitchen station prepares this. Empty means the default station. */
    preparationStationTags: zod_1.z.array(zod_1.z.string().min(1).max(64)).default([]),
    /** Scannable aliases, where the station has a scanner and the customer uses it. */
    barcodeAliases: zod_1.z.array(zod_1.z.string().min(1).max(64)).default([]),
    dietary: exports.DietaryMetadata.optional(),
    sortOrder: zod_1.z.number().int().min(0).default(0),
    /**
     * Ordered cross-sell targets for the optional upsell step (Amendment 1, D10).
     * Back Office configuration, never a theme value and never hard-coded.
     */
    suggestWithItemIds: zod_1.z.array(zod_1.z.string().min(1)).max(8).default([]),
});
/* -------------------------------------------------------------------------- */
/* Channel and published snapshot                                             */
/* -------------------------------------------------------------------------- */
exports.SelfOrderMenuChannel = zod_1.z
    .object({
    id: zod_1.z.string().min(1),
    locationId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1).max(120),
    status: exports.SelfOrderMenuChannelStatus.default('draft'),
    /** Monotonic, assigned on publish. A published version is immutable. */
    version: zod_1.z.number().int().min(0).default(0),
    publishedAt: zod_1.z.string().datetime().nullable().default(null),
    validFrom: zod_1.z.string().datetime().nullable().default(null),
    validTo: zod_1.z.string().datetime().nullable().default(null),
    defaultLocale: terminal_js_1.LocaleCode,
    availableLocales: zod_1.z.array(terminal_js_1.LocaleCode).min(1),
    currency: pricing_js_1.CurrencyCode,
    orderTypes: zod_1.z.array(exports.SelfOrderType).min(1),
    /** Terminal profiles bound to this channel. Empty means unbound, not "all". */
    terminalProfileIds: zod_1.z.array(zod_1.z.string().min(1)).default([]),
})
    .superRefine((channel, ctx) => {
    if (!channel.availableLocales.includes(channel.defaultLocale)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['defaultLocale'],
            message: 'defaultLocale must be one of availableLocales',
        });
    }
    if (channel.status === 'published' && channel.publishedAt === null) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['publishedAt'],
            message: 'a published channel must carry publishedAt',
        });
    }
});
/**
 * What a terminal actually downloads: one immutable published version, whole.
 *
 * Whole on purpose — a terminal must never observe a half-published menu, so
 * publication is atomic and the snapshot is the unit of transfer. `contentHash`
 * is the ETag the endpoint answers `304` against.
 */
exports.SelfOrderMenuSnapshot = zod_1.z.object({
    menuChannelId: zod_1.z.string().min(1),
    version: zod_1.z.number().int().min(1),
    contentHash: zod_1.z.string().regex(/^[0-9a-f]{32,128}$/),
    publishedAt: zod_1.z.string().datetime(),
    defaultLocale: terminal_js_1.LocaleCode,
    availableLocales: zod_1.z.array(terminal_js_1.LocaleCode).min(1),
    currency: pricing_js_1.CurrencyCode,
    orderTypes: zod_1.z.array(exports.SelfOrderType).min(1),
    categories: zod_1.z.array(exports.MenuCategory),
    items: zod_1.z.array(exports.MenuItemPresentation),
    modifierGroups: zod_1.z.array(exports.ModifierGroup),
});
//# sourceMappingURL=self-order-menu.js.map